import React, { useState, useEffect } from 'react';
import axios from 'axios';
import logo from '../assets/logo.png';
import logo2 from '../assets/logo2.png';
import logo3 from '../assets/logo3.png';
import './CSS/ImageConverter.css';

const ThaiOCR = (props) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [balance, setBalance] = useState(0);
  const COST_PER_EXTRACTION = 5;

  useEffect(() => {
    const state = props.location && props.location.state;
    if (state && state.email && state.balance !== undefined) {
      setEmail(state.email);
      setBalance(state.balance);
    } else {
      fetchUserData();
    }
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await fetch('/user-details', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setEmail(data.email);
        setBalance(data.balance);
      } else {
        console.error('Failed to fetch user details');
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PNG and JPEG images are allowed.');
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    setSelectedFile(file);
    setExtractedText('');
    setError('');
    setCopied(false);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleExtractClick = async () => {
    if (!selectedFile) {
      setError('Please upload a valid image first.');
      return;
    }

    // Check if user has enough balance
    if (balance < COST_PER_EXTRACTION) {
      setError('Insufficient balance for extraction. Please add credits.');
      return;
    }

    setLoading(true);
    setError('');
    setCopied(false);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post('/process-ocr', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setExtractedText(response.data.text || JSON.stringify(response.data));

      // Deduct 5 credits after extraction
      await updateBalance(balance - COST_PER_EXTRACTION);

    } catch (err) {
      setError('Failed to extract text. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateBalance = async (newBalance) => {
    try {
      const response = await fetch('/update-balance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, newBalance }),
      });

      if (response.ok) {
        setBalance(newBalance); // Update state with the new balance
      } else {
        console.error('Failed to update balance');
      }
    } catch (err) {
      console.error('Error updating balance:', err);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(extractedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <img className="logo" src={logo} alt="Logo" />
      <div style={{ marginTop: "10px", fontWeight: "bold" }}>
        Email: {email || "Loading..."} | Balance: ${balance.toFixed(2)}
      </div>

      <h2 style={{ fontSize: '2.5rem', marginTop: 20 }}>The Finest Thai-Eng OCR</h2>

      {/* Custom button to trigger file input */}
      <label
        htmlFor="file-input"
        style={{
          display: 'inline-block',
          padding: '16px 30px',
          backgroundColor: '#007BFF',
          color: '#fff',
          fontSize: '1.5rem',
          borderRadius: '8px',
          cursor: 'pointer',
          textAlign: 'center',
        }}
      >
        📂 Choose File
      </label>
      <input
        id="file-input"
        type="file"
        accept=".png,.jpg,.jpeg"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {error && <p style={{ color: 'red', fontSize: '1.2rem' }}>{error}</p>}

      {previewUrl && (
        <div style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: '1.4rem' }}>Image Preview:</h3>
          <img
            src={previewUrl}
            alt="Preview"
            style={{
              maxWidth: '100%',
              height: 'auto',
              border: '2px solid #ccc',
              borderRadius: 10,
              marginTop: 10,
            }}
          />
        </div>
      )}

      <br />
      <button
        onClick={handleExtractClick}
        disabled={!selectedFile}
        style={{
          padding: '14px 30px',
          fontSize: '1.3rem',
          backgroundColor: '#007BFF',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer',
          marginTop: 20,
        }}
      >
        🔍 Extract Text
      </button>

      {loading && <p style={{ fontSize: '1.2rem' }}>⏳ Processing...</p>}

      {extractedText && (
        <div style={{ marginTop: 40 }}>
          <h3 style={{ fontSize: '1.6rem' }}>Extracted Text (Editable):</h3>
          <textarea
            value={extractedText}
            onChange={(e) => setExtractedText(e.target.value)}
            rows={20}
            style={{
              width: '100%',
              padding: 25,
              fontSize: '1.4rem',
              lineHeight: '1.8',
              background: '#f4f4f4',
              border: '2px solid #ccc',
              borderRadius: 10,
              resize: 'vertical',
              minHeight: 400,
            }}
          />

          <button
            onClick={handleCopy}
            style={{
              marginTop: 20,
              padding: '12px 26px',
              fontSize: '1.3rem',
              backgroundColor: copied ? '#28a745' : '#333',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            {copied ? '✅ Copied!' : '📋 Copy to Clipboard'}
          </button>
        </div>
      )}

      <div style={{ marginTop: 60, textAlign: 'center' }}>
        <img src={logo2} alt="Logo 2" style={{ height: 40, marginBottom: 10 }} />
        <img src={logo3} alt="Logo 3" style={{ height: 40, marginLeft: 20 }} />
        <div style={{ marginTop: 10 }}>New Wave Consulting Group</div>
      </div>
    </div>
  );
};

export default ThaiOCR;
